"use client";

/**
 * "예약·상품 문의" 를 고른 뒤 화면 — 운영팀이 아니라 파트너에게 보낸다.
 * 최근 예약 → (없으면) 최근 본 상품 → (둘 다 없으면) 상품 상세 하단 바 안내
 * 순으로 보여준다. 예약·최근 본 상품은 같은 카드에 같은 "파트너 문의" 버튼.
 */

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { OptimizedImage } from "@/components/ui/optimized-image";
import ProductActionButtons from "@/components/product-detail/ProductActionButtons";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { getInquiryReservationsAction } from "@/app/actions/reservations";
import { fetchRecentProductsAction } from "@/app/actions/recently-viewed";
import { getOrCreateChatRoom, getOrCreateInquiryRoom } from "@/app/actions/chat";

const MAX_CARDS = 5;

interface InquiryCard {
  key: string;
  partnerName: string;
  productName: string;
  imageUrl: string | null;
  /** 채팅방 id 를 돌려준다. null = 이미 다른 곳으로 이동했거나 실패 처리됨. */
  open: () => Promise<string | null>;
}

interface SupportPartnerInquiryProps {
  authed: boolean;
  onBack: () => void;
  onStartChat: () => void;
  onClose: () => void;
}

export function SupportPartnerInquiry({
  authed,
  onBack,
  onStartChat,
  onClose,
}: SupportPartnerInquiryProps) {
  const t = useTranslations("support.partnerInquiry");
  const locale = useLocale();
  const router = useRouter();
  const { recentlyViewedIds, isLoaded } = useRecentlyViewed();
  const [openingKey, setOpeningKey] = useState<string | null>(null);

  const reservationsQuery = useQuery({
    queryKey: ["support-inquiry-reservations", locale],
    queryFn: () => getInquiryReservationsAction(locale),
    enabled: authed,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
  // 같은 상품을 여러 번 예약했으면 카드가 똑같아 보이므로(기간·지점 없음) 가장 최근 예약 하나만.
  const reservations = dedupeByProduct(reservationsQuery.data ?? []).slice(0, MAX_CARDS);
  const reservationsSettled = !authed || !reservationsQuery.isPending;

  const recentIds = recentlyViewedIds.slice(0, MAX_CARDS);
  const needRecent = reservationsSettled && reservations.length === 0;
  const recentQuery = useQuery({
    queryKey: ["support-inquiry-recent", recentIds, locale],
    queryFn: () => fetchRecentProductsAction(recentIds, locale),
    enabled: needRecent && isLoaded && recentIds.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const loading =
    !reservationsSettled ||
    (needRecent && (!isLoaded || (recentIds.length > 0 && recentQuery.isPending)));

  const cards: InquiryCard[] =
    reservations.length > 0
      ? reservations.map((r) => ({
          key: `r:${r.id}`,
          partnerName: r.partnerName,
          productName: r.productName,
          imageUrl: r.imageUrl,
          open: async () => {
            const result = await getOrCreateChatRoom(r.id);
            return result.data?.id ?? null;
          },
        }))
      : (recentQuery.data ?? []).map((p) => ({
          key: `p:${p.id}`,
          partnerName: p.partner_name ?? "",
          productName: p.name,
          imageUrl: p.images?.[0] ?? null,
          open: async () => {
            // 비로그인 — 상품 상세의 [문의] 가 로그인 모달까지 처리한다.
            if (!authed) {
              router.push(`/${locale}/products/${p.id}`);
              return null;
            }
            const result = await getOrCreateInquiryRoom(p.id, p.partner_id);
            if (!result.data?.id) return null;
            // 상품 상세와 같은 쿼리 — 채팅방이 상품 카드를 바로 그리고 문의 메시지를 보낸다.
            const params = new URLSearchParams({ inquiry: p.id });
            if (p.name) params.set("pn", p.name);
            if (p.images?.[0]) params.set("pi", p.images[0]);
            return `${result.data.id}?${params.toString()}`;
          },
        }));

  const handleInquire = async (card: InquiryCard) => {
    if (openingKey) return;
    setOpeningKey(card.key);
    try {
      const target = await card.open();
      if (target) {
        router.push(`/${locale}/messages/${target}`);
        return;
      }
    } catch (error) {
      console.error("[support] open partner chat failed:", error);
    }
    setOpeningKey(null);
    // 비로그인 상품 이동(null 반환)은 이미 router.push 됐으므로 토스트를 띄우지 않는다.
    if (authed) toast.error(t("openError"));
  };

  // 시트는 스크롤하지 않는다 — 머리(뒤로)·아래 운영팀 버튼은 고정, 가운데 목록만 스크롤.
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gray-50">
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-2 py-2">
        <button
          type="button"
          onClick={onBack}
          aria-label={t("back")}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-900 transition hover:bg-gray-100 active:bg-gray-200"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <h2 className="truncate text-[16px] font-semibold text-gray-900">{t("title")}</h2>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-3">
        {loading ? (
          <div className="mt-3 flex flex-col gap-2" aria-busy="true">
            {[0, 1].map((i) => (
              <div key={i} className="h-17 animate-pulse rounded-2xl bg-white" />
            ))}
          </div>
        ) : cards.length > 0 ? (
          <>
            <p className="mt-3 px-1 text-[13px] leading-relaxed text-gray-600">
              {reservations.length > 0 ? t("reservationsLead") : t("recentLead")}
            </p>
            <ul className="mt-2 flex flex-col gap-2">
              {cards.map((card) => (
                <li
                  key={card.key}
                  className="flex items-center gap-3 rounded-2xl bg-white p-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                >
                  <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-gray-100">
                    {card.imageUrl && (
                      <OptimizedImage
                        src={card.imageUrl}
                        alt=""
                        fill
                        sizes="48px"
                        className="object-cover"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    {card.partnerName && (
                      <p className="truncate text-[12px] text-gray-500">
                        {card.partnerName}
                      </p>
                    )}
                    <p className="truncate text-[14px] font-medium text-gray-900">
                      {card.productName}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInquire(card)}
                    disabled={openingKey !== null}
                    className="flex h-9 shrink-0 items-center justify-center rounded-xl bg-orange-50 px-3 text-[13px] font-semibold text-orange-700 ring-1 ring-orange-200 ring-inset transition active:bg-orange-100 disabled:opacity-60"
                  >
                    {openingKey === card.key ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t("inquireButton")
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className="mt-3 rounded-2xl bg-white p-4">
            <p className="text-[14px] font-semibold text-gray-900">
              {t("emptyTitle")}
            </p>
            <p className="mt-1 text-[13px] leading-relaxed text-gray-600">
              {t("emptyBody")}
            </p>
            <div className="mt-4 rounded-xl border border-gray-200 bg-white px-3 py-2.5">
              <ProductActionButtons preview />
            </div>
            <p className="mt-2 text-right text-[12px] text-orange-600">
              {t("emptyHint")}
            </p>
            <button
              type="button"
              onClick={() => {
                onClose();
                router.push(`/${locale}`);
              }}
              className="mt-4 flex h-11 w-full items-center justify-center rounded-xl bg-orange-500 text-sm font-semibold text-white transition hover:bg-orange-600 active:bg-orange-700"
            >
              {t("browse")}
            </button>
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-gray-200 bg-white px-3 py-3">
        <button
          type="button"
          onClick={onStartChat}
          className="flex h-11 w-full items-center justify-center rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-700 transition hover:bg-gray-50 active:bg-gray-100"
        >
          {t("stillContactSupport")}
        </button>
      </div>
    </div>
  );
}

/** 최신순 목록에서 상품별 첫 줄(= 가장 최근 예약)만 남긴다. */
function dedupeByProduct<T extends { productId: string }>(rows: T[]): T[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    if (seen.has(row.productId)) return false;
    seen.add(row.productId);
    return true;
  });
}
